import React, { useState } from 'react';
import { FileCheck, Download, FileText, CheckCircle, XCircle } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge } from '../shared';
import { PDFDocument } from 'pdf-lib';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

const ComparePDF = () => {
    const [file1, setFile1] = useState(null);
    const [file2, setFile2] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [comparison, setComparison] = useState(null);

    const handleFile1Selected = (files) => {
        if (files && files.length > 0) {
            setFile1(files[0]);
            toast.success('First PDF loaded');
        }
    };

    const handleFile2Selected = (files) => {
        if (files && files.length > 0) {
            setFile2(files[0]);
            toast.success('Second PDF loaded');
        }
    };

    const comparePDFs = async () => {
        if (!file1 || !file2) {
            toast.warning('Please upload both PDF files');
            return;
        }

        try {
            setIsProcessing(true);
            setProgress(10);

            const arrayBuffer1 = await file1.arrayBuffer();
            const arrayBuffer2 = await file2.arrayBuffer();

            setProgress(30);

            const pdf1 = await PDFDocument.load(arrayBuffer1);
            const pdf2 = await PDFDocument.load(arrayBuffer2);

            setProgress(60);

            const results = {
                file1: {
                    name: file1.name,
                    pages: pdf1.getPageCount(),
                    title: pdf1.getTitle() || 'N/A',
                    author: pdf1.getAuthor() || 'N/A'
                },
                file2: {
                    name: file2.name,
                    pages: pdf2.getPageCount(),
                    title: pdf2.getTitle() || 'N/A',
                    author: pdf2.getAuthor() || 'N/A'
                },
                differences: []
            };

            if (pdf1.getPageCount() !== pdf2.getPageCount()) {
                results.differences.push(`Page count differs: ${pdf1.getPageCount()} vs ${pdf2.getPageCount()}`);
            }

            if (pdf1.getTitle() !== pdf2.getTitle()) {
                results.differences.push(`Title differs`);
            }

            if (pdf1.getAuthor() !== pdf2.getAuthor()) {
                results.differences.push(`Author differs`);
            }

            if (arrayBuffer1.byteLength !== arrayBuffer2.byteLength) {
                results.differences.push(`File size differs: ${(arrayBuffer1.byteLength / 1024).toFixed(2)} KB vs ${(arrayBuffer2.byteLength / 1024).toFixed(2)} KB`);
            }

            setComparison(results);
            setProgress(100);

            if (results.differences.length === 0) {
                toast.success('PDFs appear to be identical!');
            } else {
                toast.info(`Found ${results.differences.length} difference(s)`);
            }

            setTimeout(() => setProgress(0), 1000);

        } catch (error) {
            console.error('Error:', error);
            toast.error(`Failed to compare PDFs: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div>
            {/* Header */}
            <div className="tool-header">
                <div className="tool-icon-wrapper">
                    <FileCheck size={32} />
                </div>
                <div className="tool-header-content">
                    <h1 className="tool-title">Compare PDF</h1>
                    <p className="tool-description">
                        Compare two PDF documents and highlight differences in metadata and structure.
                    </p>
                </div>
            </div>

            <div className="grid-2">
                <div>
                    <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)', marginBottom: 'var(--spacing-3)' }}>
                        First PDF
                    </h3>
                    {!file1 ? (
                        <FileUploadZone
                            onFilesSelected={handleFile1Selected}
                            acceptedTypes={['application/pdf']}
                            multiple={false}
                            title="Drop first PDF here"
                            description="or click to browse"
                        />
                    ) : (
                        <Alert variant="success">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>{file1.name}</span>
                                <Button variant="ghost" size="sm" onClick={() => setFile1(null)}>Change</Button>
                            </div>
                        </Alert>
                    )}
                </div>

                <div>
                    <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)', marginBottom: 'var(--spacing-3)' }}>
                        Second PDF
                    </h3>
                    {!file2 ? (
                        <FileUploadZone
                            onFilesSelected={handleFile2Selected}
                            acceptedTypes={['application/pdf']}
                            multiple={false}
                            title="Drop second PDF here"
                            description="or click to browse"
                        />
                    ) : (
                        <Alert variant="success">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>{file2.name}</span>
                                <Button variant="ghost" size="sm" onClick={() => setFile2(null)}>Change</Button>
                            </div>
                        </Alert>
                    )}
                </div>
            </div>

            {file1 && file2 && (
                <>
                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                        </div>
                    )}

                    {!comparison && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <Button variant="primary" size="lg" onClick={comparePDFs} disabled={isProcessing} loading={isProcessing} fullWidth>
                                <FileCheck size={20} />
                                Compare PDFs
                            </Button>
                        </div>
                    )}

                    {comparison && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <Card>
                                <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)', marginBottom: 'var(--spacing-4)' }}>
                                    Comparison Results
                                </h3>

                                <div className="grid-2" style={{ marginBottom: 'var(--spacing-4)' }}>
                                    <div>
                                        <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-2)', color: 'var(--color-text-secondary)' }}>
                                            {comparison.file1.name}
                                        </h4>
                                        <ul style={{ fontSize: 'var(--text-sm)', lineHeight: 1.8 }}>
                                            <li>Pages: {comparison.file1.pages}</li>
                                            <li>Title: {comparison.file1.title}</li>
                                            <li>Author: {comparison.file1.author}</li>
                                        </ul>
                                    </div>

                                    <div>
                                        <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-2)', color: 'var(--color-text-secondary)' }}>
                                            {comparison.file2.name}
                                        </h4>
                                        <ul style={{ fontSize: 'var(--text-sm)', lineHeight: 1.8 }}>
                                            <li>Pages: {comparison.file2.pages}</li>
                                            <li>Title: {comparison.file2.title}</li>
                                            <li>Author: {comparison.file2.author}</li>
                                        </ul>
                                    </div>
                                </div>

                                {comparison.differences.length === 0 ? (
                                    <Alert variant="success">
                                        <CheckCircle size={20} style={{ display: 'inline', marginRight: 'var(--spacing-2)' }} />
                                        <strong>PDFs are identical</strong> (based on metadata and structure)
                                    </Alert>
                                ) : (
                                    <Alert variant="warning">
                                        <XCircle size={20} style={{ display: 'inline', marginRight: 'var(--spacing-2)' }} />
                                        <strong>Differences found:</strong>
                                        <ul style={{ marginTop: 'var(--spacing-2)', paddingLeft: 'var(--spacing-5)' }}>
                                            {comparison.differences.map((diff, i) => (
                                                <li key={i}>{diff}</li>
                                            ))}
                                        </ul>
                                    </Alert>
                                )}
                            </Card>

                            <div style={{ marginTop: 'var(--spacing-4)' }}>
                                <Button variant="secondary" size="sm" onClick={() => setComparison(null)}>
                                    Compare Again
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ComparePDF;
